import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthProvider';

// Onboarding modes for the unified wizard
export type OnboardingMode = 'none' | 'full' | 'profile' | 'tour';

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  target?: string; // CSS selector for highlighting
  position?: 'top' | 'bottom' | 'left' | 'right';
  action?: string;
}

interface OnboardingContextType {
  // Unified onboarding state
  onboardingMode: OnboardingMode;
  startUnifiedOnboarding: (mode: OnboardingMode) => void;
  completeUnifiedOnboarding: () => void;
  skipUnifiedOnboarding: () => void;

  // Legacy support (deprecated but kept for compatibility)
  isOnboardingActive: boolean;
  currentStep: number;
  steps: OnboardingStep[];
  isBetaUser: boolean;
  startOnboarding: () => void;
  nextStep: () => void;
  previousStep: () => void;
  skipOnboarding: () => void;
  completeOnboarding: () => void;
  isCompleted: boolean;
  // Profile setup wizard (deprecated - use startUnifiedOnboarding('profile'))
  showProfileSetup: boolean;
  startProfileSetup: () => void;
  completeProfileSetup: () => void;
  skipProfileSetup: () => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const ONBOARDING_ENABLED = true;

const defaultSteps: OnboardingStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to Vitly! 🎉',
    description: 'Let\'s take a quick tour to help you get started with finding the perfect meeting spots.',
    position: 'bottom'
  },
  {
    id: 'dashboard',
    title: 'Your Dashboard',
    description: 'This is your command center. Here you can see all your meetups, create new ones, and manage your schedule.',
    target: '[data-onboarding="dashboard"]',
    position: 'right'
  },
  {
    id: 'create-meetup',
    title: 'Create Your First Meetup',
    description: 'Click here to create your first meetup. Choose from templates or start from scratch.',
    target: '[data-onboarding="create-meetup"]',
    position: 'bottom',
    action: 'create-meetup'
  },
  {
    id: 'places',
    title: 'Discover Places',
    description: 'Explore amazing places in your area. Filter by cuisine, price, and more to find the perfect spot.',
    target: '[data-onboarding="places"]',
    position: 'bottom'
  },
  {
    id: 'friends',
    title: 'Connect with Friends',
    description: 'Invite friends to your meetups and build your social network within Vitly.',
    target: '[data-onboarding="friends"]',
    position: 'left'
  },
  {
    id: 'feedback',
    title: 'Share Your Feedback',
    description: 'We\'re always improving! Use the feedback button anytime to share your thoughts and suggestions.',
    target: '[data-onboarding="feedback"]',
    position: 'left'
  },
  {
    id: 'complete',
    title: 'You\'re All Set! 🚀',
    description: 'You\'re ready to start planning amazing meetups. Have fun exploring Vitly!',
    position: 'bottom'
  }
];


export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  // Unified onboarding state
  const [onboardingMode, setOnboardingMode] = useState<OnboardingMode>('none');

  // Legacy states (kept for backward compatibility)
  const [isOnboardingActive, setIsOnboardingActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const isBetaUser = false; // Beta mode disabled
  const [showProfileSetup, setShowProfileSetup] = useState(false);

  // Check if user has completed onboarding
  useEffect(() => {
    if (!ONBOARDING_ENABLED) {
      setIsOnboardingActive(false);
      setIsCompleted(true);
      return;
    }

    const checkUserStatus = async () => {
      if (user) {
        const onboardingCompleted = localStorage.getItem(`vitly-onboarding-${user.id}`);
        const profileSetupCompleted = localStorage.getItem(`vitly-profile-setup-${user.id}`);
        const isNewUser = user.created_at && new Date(user.created_at) > new Date(Date.now() - 24 * 60 * 60 * 1000); // Created within last 24 hours

        const forceOnboarding = new URLSearchParams(window.location.search).get('force_onboarding');
        const shouldForceOnboarding = forceOnboarding === 'full' || forceOnboarding === 'profile' || forceOnboarding === 'tour';

        // Skip onboarding for test users or when skip parameter is present
        const isTestUser = user.email === 'getvitly@gmail.com' || user.email?.includes('@test.') || user.email?.includes('testuser');
        const skipOnboardingParam = new URLSearchParams(window.location.search).get('skip_onboarding') === 'true';
        const isE2ETest = window.location.search.includes('e2e_test=true') || navigator.userAgent.includes('Playwright');

        // Check if user needs profile setup (missing firstName or lastName) AND is a new user
        const userMetadata = user.user_metadata || {};
        const needsProfileSetup = !profileSetupCompleted && (!userMetadata.first_name || !userMetadata.last_name) && isNewUser;

        if (shouldForceOnboarding) {
          setTimeout(() => {
            setOnboardingMode(forceOnboarding as OnboardingMode);
          }, 800);
        } else if (!isTestUser && !skipOnboardingParam && !isE2ETest) {
          if (needsProfileSetup && !onboardingCompleted) {
            // New user with incomplete profile - show full unified onboarding
            setTimeout(() => {
              setOnboardingMode('full');
            }, 1500);
          } else if (!onboardingCompleted && isNewUser) {
            // New user with complete profile - show tour only
            setTimeout(() => {
              setOnboardingMode('tour');
            }, 2000);
          }
        }

        // Auto-complete onboarding for test users
        if (!shouldForceOnboarding && (isTestUser || skipOnboardingParam || isE2ETest)) {
          localStorage.setItem(`vitly-onboarding-${user.id}`, 'completed');
        }

        setIsCompleted(!!onboardingCompleted);
      }
    };

    checkUserStatus();
  }, [user]);

  const startOnboarding = () => {
    if (!ONBOARDING_ENABLED) {
      setIsOnboardingActive(false);
      setIsCompleted(true);
      return;
    }
    setIsOnboardingActive(true);
    setCurrentStep(0);
  };

  const nextStep = () => {
    if (!ONBOARDING_ENABLED) {
      completeOnboarding();
      return;
    }
    if (currentStep < defaultSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeOnboarding();
    }
  };

  const previousStep = () => {
    if (!ONBOARDING_ENABLED) {
      return;
    }
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const skipOnboarding = () => {
    setIsOnboardingActive(false);
    setCurrentStep(0);
    if (user) {
      localStorage.setItem(`vitly-onboarding-${user.id}`, 'skipped');
    }
  };

  const completeOnboarding = () => {
    setIsOnboardingActive(false);
    setCurrentStep(0);
    setIsCompleted(true);
    if (user) {
      localStorage.setItem(`vitly-onboarding-${user.id}`, 'completed');
    }
  };

  // Unified onboarding methods
  const startUnifiedOnboarding = (mode: OnboardingMode) => {
    if (!ONBOARDING_ENABLED) {
      return;
    }
    setOnboardingMode(mode);
  };

  const completeUnifiedOnboarding = () => {
    setOnboardingMode('none');
    setIsCompleted(true);
    if (user) {
      localStorage.setItem(`vitly-onboarding-${user.id}`, 'completed');
      localStorage.setItem(`vitly-profile-setup-${user.id}`, 'completed');
    }
  };

  const skipUnifiedOnboarding = () => {
    setOnboardingMode('none');
    if (user) {
      localStorage.setItem(`vitly-onboarding-${user.id}`, 'skipped');
      localStorage.setItem(`vitly-profile-setup-${user.id}`, 'skipped');
    }
  };

  // Legacy methods (kept for backward compatibility)
  const startProfileSetup = () => {
    // Use unified wizard in profile mode
    setOnboardingMode('profile');
  };

  const completeProfileSetup = () => {
    setShowProfileSetup(false);
    if (user) {
      localStorage.setItem(`vitly-profile-setup-${user.id}`, 'completed');
    }
    // After profile setup, start the main onboarding tour if user is new
    const isNewUser = user?.created_at && new Date(user.created_at) > new Date(Date.now() - 24 * 60 * 60 * 1000);
    const onboardingCompleted = user ? localStorage.getItem(`vitly-onboarding-${user.id}`) : null;
    if (isNewUser && !onboardingCompleted) {
      setTimeout(() => {
        startOnboarding();
      }, 1000);
    }
  };

  const skipProfileSetup = () => {
    setShowProfileSetup(false);
    if (user) {
      localStorage.setItem(`vitly-profile-setup-${user.id}`, 'skipped');
    }
  };

  const value: OnboardingContextType = {
    // Unified onboarding
    onboardingMode,
    startUnifiedOnboarding,
    completeUnifiedOnboarding,
    skipUnifiedOnboarding,

    // Legacy support
    isOnboardingActive,
    currentStep,
    steps: defaultSteps,
    isBetaUser,
    startOnboarding,
    nextStep,
    previousStep,
    skipOnboarding,
    completeOnboarding,
    isCompleted,
    showProfileSetup,
    startProfileSetup,
    completeProfileSetup,
    skipProfileSetup
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};
